import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const vendorApi = createApi({
  reducerPath: "vendorApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Vendor", "VendorStats", "PendingRequests"],
  endpoints: (builder) => ({
    // Get all vendors
    getAllVendors: builder.query({
      // Keep the directory cached for 5 min after unmount so bouncing
      // between Directory / Scorecard / a vendor detail doesn't re-fire
      // the request and re-show the full loading state every time.
      keepUnusedDataFor: 300,
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append("status", params.status);
        if (params.category) queryParams.append("category", params.category);
        if (params.search) queryParams.append("search", params.search);
        if (params.minRating) queryParams.append("minRating", params.minRating);
        if (params.sortBy) queryParams.append("sortBy", params.sortBy);
        if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);
        return `/vendors?${queryParams.toString()}`;
      },
      transformResponse: (response) => ({ data: response.data || response.vendors || [] }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: "Vendor", id: _id })),
              { type: "Vendor", id: "LIST" },
            ]
          : [{ type: "Vendor", id: "LIST" }],
    }),

    // Get single vendor
    getVendorById: builder.query({
      query: (id) => `/vendors/${id}`,
      transformResponse: (response) => response.data || response.vendor || null,
      providesTags: (result, error, id) => [{ type: "Vendor", id }],
    }),

    // Create vendor
    createVendor: builder.mutation({
      query: (vendorData) => ({
        url: "/vendors",
        method: "POST",
        body: vendorData,
      }),
      transformResponse: (response) => response.data || response,
      invalidatesTags: [
        { type: "Vendor", id: "LIST" },
        { type: "VendorStats" },
      ],
    }),

    // Update vendor
    updateVendor: builder.mutation({
      query: ({ id, ...vendorData }) => ({
        url: `/vendors/${id}`,
        method: "PUT",
        body: vendorData,
      }),
      transformResponse: (response) => response.data || response,
      invalidatesTags: (result, error, { id }) => [
        { type: "Vendor", id },
        { type: "Vendor", id: "LIST" },
      ],
    }),

    // Delete vendor
    deleteVendor: builder.mutation({
      query: (id) => ({
        url: `/vendors/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Vendor", id: "LIST" },
        { type: "VendorStats" },
      ],
    }),

    // Add review manually
    addReview: builder.mutation({
      query: ({ vendorId, ...reviewData }) => ({
        url: `/vendors/${vendorId}/review`,
        method: "POST",
        body: reviewData,
      }),
      transformResponse: (response) => response.data || response,
      invalidatesTags: (result, error, { vendorId }) => [
        { type: "Vendor", id: vendorId },
        { type: "Vendor", id: "LIST" },
        { type: "VendorStats" },
      ],
    }),

    // Delete review
    deleteReview: builder.mutation({
      query: ({ vendorId, reviewId }) => ({
        url: `/vendors/${vendorId}/review/${reviewId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { vendorId }) => [
        { type: "Vendor", id: vendorId },
        { type: "VendorStats" },
      ],
    }),

    // Create review request (send to client)
    createReviewRequest: builder.mutation({
      query: ({ vendorId, ...requestData }) => ({
        url: `/vendors/${vendorId}/request-review`,
        method: "POST",
        body: requestData,
      }),
      transformResponse: (response) => response.data || response,
      invalidatesTags: (result, error, { vendorId }) => [
        { type: "Vendor", id: vendorId },
        { type: "PendingRequests" },
      ],
    }),

    // Resend review request
    resendReviewRequest: builder.mutation({
      query: ({ vendorId, requestId }) => ({
        url: `/vendors/${vendorId}/request/${requestId}/resend`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "PendingRequests" }],
    }),

    // Cancel review request
    cancelReviewRequest: builder.mutation({
      query: ({ vendorId, requestId }) => ({
        url: `/vendors/${vendorId}/request/${requestId}/cancel`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { vendorId }) => [
        { type: "Vendor", id: vendorId },
        { type: "PendingRequests" },
      ],
    }),

    deleteReviewRequest: builder.mutation({
      query: ({ vendorId, requestId }) => ({
        url: `/vendors/${vendorId}/request/${requestId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { vendorId }) => [
        { type: "Vendor", id: vendorId },
        { type: "PendingRequests" },
      ],
    }),

    // Get stats
    getVendorStats: builder.query({
      query: () => "/vendors/stats",
      transformResponse: (response) => ({ data: { summary: response.data?.summary || response.summary || {} } }),
      providesTags: [{ type: "VendorStats" }],
    }),

    // Get pending requests
    getPendingRequests: builder.query({
      query: () => "/vendors/pending-requests",
      transformResponse: (response) => response.data || { requests: [] },
      providesTags: [{ type: "PendingRequests" }],
    }),

    // Toggle favorite
    toggleFavorite: builder.mutation({
      query: (id) => ({
        url: `/vendors/${id}/favorite`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Vendor", id },
        { type: "Vendor", id: "LIST" },
      ],
    }),

    // Record referral
    recordReferral: builder.mutation({
      query: (id) => ({
        url: `/vendors/${id}/referral`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Vendor", id }],
    }),

    // Public: Get vendor by token
    getVendorByToken: builder.query({
      query: (token) => `/vendors/public/review/${token}`,
      transformResponse: (response) => response.data || response,
    }),

    // Public: Submit review by token
    submitReviewByToken: builder.mutation({
      query: ({ token, ...reviewData }) => ({
        url: `/vendors/public/review/${token}`,
        method: "POST",
        body: reviewData,
      }),
    }),
  }),
});

export const {
  useGetAllVendorsQuery,
  useGetVendorByIdQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useDeleteVendorMutation,
  useAddReviewMutation,
  useDeleteReviewMutation,
  useCreateReviewRequestMutation,
  useResendReviewRequestMutation,
  useCancelReviewRequestMutation,
  useDeleteReviewRequestMutation,
  useGetVendorStatsQuery,
  useGetPendingRequestsQuery,
  useToggleFavoriteMutation,
  useRecordReferralMutation,
  useGetVendorByTokenQuery,
  useSubmitReviewByTokenMutation,
} = vendorApi;
